/**
 * QuotaSafeStorage.ts — BETA GATE HARDENING™ sprint (Critical Issue #1).
 *
 * Centralized quota-aware persistence layer for every localStorage-backed
 * engine. Responsibilities:
 *   • Estimate storage usage + remaining capacity (lightweight, no extra
 *     serialization — sizes come from the strings we already have).
 *   • Detect approaching quota BEFORE writing and compact proactively.
 *   • Never throw QuotaExceededError to the UI — recovery ladder is
 *     write → compact(level 1) → retry → compact(level 2) → retry → record
 *     diagnostic + health warning and keep running.
 *   • Never partially write (localStorage.setItem is atomic; on failure the
 *     previous value is untouched — we never delete-then-write).
 *   • Retention policy lives with the OWNER of the data: engines pass a
 *     `shrink(state, level)` function that removes oldest records first and
 *     preserves the newest. This layer never guesses at domain semantics.
 *   • Migration-ready: `readJSON`/`writeJSON` are the single choke point a
 *     future IndexedDB transition swaps out behind the same surface.
 *
 * Engines keep their in-memory behavior and public APIs unchanged — only the
 * persistence call site moves here.
 */

/** Conservative browser localStorage quota assumption (~5 MB of UTF-16). */
export const ESTIMATED_QUOTA_BYTES = 5 * 1024 * 1024;
/** Soft limit — approaching this triggers proactive compaction. */
export const SOFT_LIMIT_BYTES = Math.floor(ESTIMATED_QUOTA_BYTES * 0.8);
const MAX_WARNINGS = 10;

/** Oldest-first reducer supplied by the data owner. Level 2 is more aggressive than level 1. */
export type ShrinkFn<T> = (state: T, level: 1 | 2) => T;

export interface QuotaSafeDiagnostics {
  estimatedUsageBytes: number;
  estimatedQuotaBytes: number;
  estimatedAvailableBytes: number;
  softLimitBytes: number;
  usagePercent: number;
  largestConsumers: Array<{ key: string; bytes: number; managed: boolean }>;
  managedKeys: string[];
  compactionCount: number;
  failedWrites: number;
  quotaWarnings: string[];
  lastCleanupAt: number | null;
  lastError: string | null;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key'> & { readonly length: number };

/** Browser quota failures come in several shapes — detect them all. */
export function isQuotaError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const code = (err as { code?: number }).code;
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014
  );
}

/** UTF-16 storage cost estimate for a key/value pair. */
function pairBytes(key: string, value: string): number {
  return (key.length + value.length) * 2;
}

export class QuotaSafeStorage {
  private compactionCount = 0;
  private failedWrites = 0;
  private warnings: string[] = [];
  private lastCleanupAt: number | null = null;
  private lastError: string | null = null;
  private managed = new Map<string, string>(); // key → owner label

  constructor(
    private readonly storage?: StorageLike,
    private readonly now: () => number = () => Date.now(),
  ) {}

  private area(): StorageLike | null {
    if (this.storage) return this.storage;
    return typeof localStorage === 'undefined' ? null : localStorage;
  }

  /** Engines register their keys so diagnostics can attribute consumers. */
  registerManagedKey(key: string, ownerLabel: string): void {
    this.managed.set(key, ownerLabel);
  }

  /** Total estimated usage across ALL localStorage keys (cheap linear scan). */
  estimateUsageBytes(): number {
    const area = this.area();
    if (!area) return 0;
    let total = 0;
    try {
      for (let i = 0; i < area.length; i += 1) {
        const key = area.key(i);
        if (key == null) continue;
        total += pairBytes(key, area.getItem(key) ?? '');
      }
    } catch { /* storage unavailable — report what we have */ }
    return total;
  }

  /**
   * Quota-safe JSON write. Returns true when the state (possibly compacted)
   * was persisted, false when storage is genuinely unwritable. NEVER throws.
   */
  writeJSON<T>(key: string, state: T, shrink?: ShrinkFn<T>): boolean {
    const area = this.area();
    if (!area) return false;

    let current: T = state;
    let serialized: string;
    try {
      serialized = JSON.stringify(current);
    } catch (err) {
      this.recordFailure(key, `serialize: ${err instanceof Error ? err.message : String(err)}`);
      return false;
    }

    // Proactive compaction: if the projected total would cross the soft
    // limit, shed oldest records BEFORE the browser has to throw at us.
    if (shrink) {
      const existing = pairBytes(key, this.safeGet(area, key) ?? '');
      const projected = this.estimateUsageBytes() - existing + pairBytes(key, serialized);
      if (projected > SOFT_LIMIT_BYTES) {
        current = shrink(current, 1);
        serialized = JSON.stringify(current);
        this.noteCompaction(key, 'approaching quota (proactive)');
      }
    }

    // Recovery ladder: write → compact L1 → retry → compact L2 → retry.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        area.setItem(key, serialized);
        return true;
      } catch (err) {
        if (!isQuotaError(err) || !shrink || attempt === 2) {
          this.recordFailure(key, err instanceof Error ? `${err.name}: ${err.message}` : String(err));
          return false;
        }
        const level = (attempt + 1) as 1 | 2;
        current = shrink(current, level);
        serialized = JSON.stringify(current);
        this.noteCompaction(key, `QuotaExceededError recovery (level ${level})`);
      }
    }
    return false;
  }

  /** Corruption-safe JSON read — malformed payloads yield null, never a throw. */
  readJSON<T>(key: string): T | null {
    const area = this.area();
    if (!area) return null;
    try {
      const raw = area.getItem(key);
      if (raw == null) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  getDiagnostics(): QuotaSafeDiagnostics {
    const area = this.area();
    const consumers: Array<{ key: string; bytes: number; managed: boolean }> = [];
    if (area) {
      try {
        for (let i = 0; i < area.length; i += 1) {
          const key = area.key(i);
          if (key == null) continue;
          consumers.push({ key, bytes: pairBytes(key, area.getItem(key) ?? ''), managed: this.managed.has(key) });
        }
      } catch { /* storage unavailable */ }
    }
    consumers.sort((a, b) => b.bytes - a.bytes);
    const usage = consumers.reduce((sum, c) => sum + c.bytes, 0);
    return {
      estimatedUsageBytes: usage,
      estimatedQuotaBytes: ESTIMATED_QUOTA_BYTES,
      estimatedAvailableBytes: Math.max(0, ESTIMATED_QUOTA_BYTES - usage),
      softLimitBytes: SOFT_LIMIT_BYTES,
      usagePercent: Math.round((usage / ESTIMATED_QUOTA_BYTES) * 100),
      largestConsumers: consumers.slice(0, 10),
      managedKeys: Array.from(this.managed.keys()),
      compactionCount: this.compactionCount,
      failedWrites: this.failedWrites,
      quotaWarnings: [...this.warnings],
      lastCleanupAt: this.lastCleanupAt,
      lastError: this.lastError,
    };
  }

  // ── internals ────────────────────────────────────────────────────────────────

  private safeGet(area: StorageLike, key: string): string | null {
    try { return area.getItem(key); } catch { return null; }
  }

  private noteCompaction(key: string, reason: string): void {
    this.compactionCount += 1;
    this.lastCleanupAt = this.now();
    this.pushWarning(`Compacted "${key}" — ${reason}.`);
  }

  private recordFailure(key: string, detail: string): void {
    this.failedWrites += 1;
    this.lastError = `${key}: ${detail}`;
    this.pushWarning(`Persist failed for "${key}" — ${detail}. Engine continues in memory.`);
    // Health warning, never a crash: visible in console + Dev Center.
    console.warn(`[quota-safe-storage] ${this.lastError}`);
  }

  private pushWarning(message: string): void {
    this.warnings.push(`${new Date(this.now()).toISOString()} ${message}`);
    if (this.warnings.length > MAX_WARNINGS) this.warnings = this.warnings.slice(-MAX_WARNINGS);
  }
}

/** App-wide singleton — the one persistence gate for managed engine state. */
export const quotaSafeStorage = new QuotaSafeStorage();
